import React, { useState, useEffect, useContext } from 'react';
import { AppContext } from '../context/AppContext';
import {
  EnvelopeIcon,
  CheckCircleIcon,
  XCircleIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon,
  ChartBarIcon,
  Cog6ToothIcon,
  PencilSquareIcon,
  PaperAirplaneIcon
} from '@heroicons/react/24/outline';

const EmailSettings = () => {
  const { token, demoMode } = useContext(AppContext);
  const [loading, setLoading] = useState(true);
  const [config, setConfig] = useState(null);
  const [showWizard, setShowWizard] = useState(false);
  const [wizardStep, setWizardStep] = useState(1);
  const [formData, setFormData] = useState({
    apiKey: '',
    fromAddress: '',
    fromName: 'ParishRecorder',
    replyTo: '',
    tier: 'free'
  });
  const [testEmail, setTestEmail] = useState('');
  const [testingConfig, setTestingConfig] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [quota, setQuota] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    loadConfig();
    loadQuota();
  }, []);

  const loadConfig = async () => {
    try {
      const response = await fetch('/api/email/config', {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await response.json();

      if (data.success) {
        setConfig(data.config);
        if (!data.config.enabled || data.config.enabled === 'false') {
          setShowWizard(true);
        }
      }
    } catch (err) {
      setError('Failed to load email configuration');
    } finally {
      setLoading(false);
    }
  };

  const loadQuota = async () => {
    try {
      const response = await fetch('/api/email/quota', {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await response.json();

      if (data.success) {
        setQuota(data.usage);
      }
    } catch (err) {
      // Quota might not be available if email is not configured
      console.log('Quota not available');
    }
  };

  const handleSaveConfig = async () => {
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const response = await fetch('/api/email/config', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          enabled: true,
          ...formData
        })
      });

      const data = await response.json();

      if (data.success) {
        setSuccess('Email configuration saved successfully');
        await loadConfig();
        setShowWizard(false);
      } else {
        setError(data.error || 'Failed to save configuration');
      }
    } catch (err) {
      setError('Failed to save email configuration');
    } finally {
      setLoading(false);
    }
  };

  const handleTestEmail = async () => {
    setTestingConfig(true);
    setTestResult(null);

    try {
      // Build request body - include config if in wizard mode (config not saved yet)
      const requestBody = {
        email: testEmail,
        name: 'Admin'
      };

      // When in wizard mode, pass the config directly for testing
      if (showWizard && formData.apiKey && formData.fromAddress) {
        requestBody.config = {
          apiKey: formData.apiKey,
          fromAddress: formData.fromAddress,
          fromName: formData.fromName || 'ParishRecorder',
          replyTo: formData.replyTo || ''
        };
      }

      const response = await fetch('/api/email/test', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(requestBody)
      });

      const data = await response.json();

      if (data.success) {
        setTestResult({ success: true, message: 'Test email sent successfully!' });
      } else {
        setTestResult({ success: false, message: data.error || 'Failed to send test email' });
      }
    } catch (err) {
      setTestResult({ success: false, message: 'Failed to send test email' });
    } finally {
      setTestingConfig(false);
    }
  };

  const handleDisableEmail = async () => {
    if (!window.confirm('Are you sure you want to disable email functionality?')) {
      return;
    }

    try {
      const response = await fetch('/api/email/config', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ enabled: false })
      });

      if (response.ok) {
        setSuccess('Email functionality disabled');
        await loadConfig();
      }
    } catch (err) {
      setError('Failed to disable email');
    }
  };

  // Render functions for wizard steps (inline to avoid focus loss issues)
  const renderWizardStep1 = () => (
    <div className="space-y-4">
      <div className="text-center">
        <EnvelopeIcon className="mx-auto h-12 w-12 text-indigo-600" />
        <h3 className="mt-2 text-lg font-semibold text-gray-900">Enable Email Functionality</h3>
        <p className="mt-1 text-sm text-gray-500">
          Send professional emails to your contacts for password resets, invoices, and payment confirmations.
        </p>
      </div>

      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <h4 className="text-sm font-semibold text-blue-900 mb-2">Benefits of Email Integration:</h4>
        <ul className="text-sm text-blue-800 space-y-1">
          <li>✓ Password reset emails for users</li>
          <li>✓ Automated invoice delivery</li>
          <li>✓ Payment confirmation receipts</li>
          <li>✓ Welcome emails for new users</li>
          <li>✓ Professional branded email templates</li>
          <li>✓ Track email delivery and opens</li>
        </ul>
      </div>

      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
        <h4 className="text-sm font-semibold text-yellow-900 mb-2">What you'll need:</h4>
        <ul className="text-sm text-yellow-800 space-y-1">
          <li>• Resend API key (free tier: 3,000 emails/month)</li>
          <li>• Verified sender email address</li>
          <li>• About 5 minutes to set up</li>
        </ul>
      </div>

      <button
        onClick={() => setWizardStep(2)}
        className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
      >
        Get Started
      </button>
    </div>
  );

  const renderWizardStep2 = () => (
    <div className="space-y-4">
      <h3 className="text-lg font-semibold text-gray-900">Step 2: Create Resend Account</h3>

      <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 space-y-3">
        <p className="text-sm text-gray-700">
          <strong>1.</strong> Go to <a href="https://resend.com" target="_blank" rel="noopener noreferrer" className="text-indigo-600 hover:underline">resend.com</a> and create a free account
        </p>
        <p className="text-sm text-gray-700">
          <strong>2.</strong> Verify your email address
        </p>
        <p className="text-sm text-gray-700">
          <strong>3.</strong> Add your domain or use Resend's test domain
        </p>
        <p className="text-sm text-gray-700">
          <strong>4.</strong> Create an API key from the dashboard
        </p>
      </div>

      <div className="bg-green-50 border border-green-200 rounded-lg p-4">
        <p className="text-sm text-green-800">
          💡 <strong>Tip:</strong> The free tier includes 3,000 emails per month, which is perfect for most cemetery management needs.
        </p>
      </div>

      <div className="flex gap-2">
        <button
          onClick={() => setWizardStep(1)}
          className="flex-1 py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
        >
          Back
        </button>
        <button
          onClick={() => setWizardStep(3)}
          className="flex-1 py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700"
        >
          Next: Configure
        </button>
      </div>
    </div>
  );

  const renderWizardStep3 = () => (
    <div className="space-y-4">
      <h3 className="text-lg font-semibold text-gray-900">Step 3: Configure Email Settings</h3>

      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700">
            Resend API Key *
          </label>
          <input
            type="text"
            value={formData.apiKey}
            onChange={(e) => setFormData({ ...formData, apiKey: e.target.value })}
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
            placeholder="re_..."
          />
          <p className="mt-1 text-xs text-gray-500">
            Your Resend API key (starts with "re_")
          </p>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700">
            From Email Address *
          </label>
          <input
            type="email"
            value={formData.fromAddress}
            onChange={(e) => setFormData({ ...formData, fromAddress: e.target.value })}
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
            placeholder="noreply@yourdomain.com"
          />
          <p className="mt-1 text-xs text-gray-500">
            Email address that will appear as the sender
          </p>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700">
            From Name
          </label>
          <input
            type="text"
            value={formData.fromName}
            onChange={(e) => setFormData({ ...formData, fromName: e.target.value })}
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
            placeholder="ParishRecorder"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700">
            Reply-To Email (optional)
          </label>
          <input
            type="email"
            value={formData.replyTo}
            onChange={(e) => setFormData({ ...formData, replyTo: e.target.value })}
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
            placeholder="support@yourdomain.com"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700">
            Resend Tier
          </label>
          <select
            value={formData.tier}
            onChange={(e) => setFormData({ ...formData, tier: e.target.value })}
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
          >
            <option value="free">Free (3,000 emails/month)</option>
            <option value="starter">Starter (50,000 emails/month)</option>
            <option value="pro">Pro (100,000 emails/month)</option>
            <option value="enterprise">Enterprise (Unlimited)</option>
          </select>
        </div>
      </div>

      <div className="flex gap-2">
        <button
          onClick={() => setWizardStep(2)}
          className="flex-1 py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
        >
          Back
        </button>
        <button
          onClick={() => setWizardStep(4)}
          disabled={!formData.apiKey || !formData.fromAddress}
          className="flex-1 py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 disabled:bg-gray-400"
        >
          Next: Test
        </button>
      </div>
    </div>
  );

  const renderWizardStep4 = () => (
    <div className="space-y-4">
      <h3 className="text-lg font-semibold text-gray-900">Step 4: Test Configuration</h3>

      <p className="text-sm text-gray-600">
        Let's verify your email configuration works by sending a test email.
      </p>

      <div>
        <label className="block text-sm font-medium text-gray-700">
          Test Email Address
        </label>
        <input
          type="email"
          value={testEmail}
          onChange={(e) => setTestEmail(e.target.value)}
          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
          placeholder="your@email.com"
        />
      </div>

      <button
        onClick={handleTestEmail}
        disabled={!testEmail || testingConfig}
        className="w-full flex justify-center items-center gap-2 py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-green-600 hover:bg-green-700 disabled:bg-gray-400"
      >
        {testingConfig ? (
          <>Sending...</>
        ) : (
          <>
            <PaperAirplaneIcon className="h-5 w-5" />
            Send Test Email
          </>
        )}
      </button>

      {testResult && (
        <div className={`p-4 rounded-md ${testResult.success ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'}`}>
          <div className="flex">
            {testResult.success ? (
              <CheckCircleIcon className="h-5 w-5 text-green-400" />
            ) : (
              <XCircleIcon className="h-5 w-5 text-red-400" />
            )}
            <div className="ml-3">
              <p className={`text-sm ${testResult.success ? 'text-green-800' : 'text-red-800'}`}>
                {testResult.message}
              </p>
            </div>
          </div>
        </div>
      )}

      <div className="flex gap-2">
        <button
          onClick={() => setWizardStep(3)}
          className="flex-1 py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
        >
          Back
        </button>
        <button
          onClick={handleSaveConfig}
          disabled={!testResult?.success || loading}
          className="flex-1 py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 disabled:bg-gray-400"
        >
          {loading ? 'Saving...' : 'Complete Setup'}
        </button>
      </div>
    </div>
  );

  if (loading && !config) {
    return (
      <div className="flex justify-center items-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  if (showWizard) {
    return (
      <div className="max-w-2xl mx-auto">
        <div className="bg-white shadow rounded-lg p-6">
          <div className="mb-6">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold text-gray-900">Email Setup Wizard</h2>
              <span className="text-sm text-gray-500">Step {wizardStep} of 4</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div
                className="bg-indigo-600 h-2 rounded-full transition-all duration-300"
                style={{ width: `${(wizardStep / 4) * 100}%` }}
              ></div>
            </div>
          </div>

          {wizardStep === 1 && renderWizardStep1()}
          {wizardStep === 2 && renderWizardStep2()}
          {wizardStep === 3 && renderWizardStep3()}
          {wizardStep === 4 && renderWizardStep4()}

          {error && (
            <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-md">
              <p className="text-sm text-red-800">{error}</p>
            </div>
          )}

          {success && (
            <div className="mt-4 p-3 bg-green-50 border border-green-200 rounded-md">
              <p className="text-sm text-green-800">{success}</p>
            </div>
          )}
        </div>
      </div>
    );
  }

  const isEnabled = config?.enabled === 'true' || config?.enabled === true;

  return (
    <div className="space-y-6">
      {/* Demo Mode Warning */}
      {demoMode?.enabled && (
        <div className="border-l-4 border-yellow-400 bg-yellow-50 p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <ExclamationTriangleIcon className="h-5 w-5 text-yellow-400" />
            </div>
            <div className="ml-3">
              <p className="text-sm font-medium text-yellow-800">
                Email Disabled in Demo Mode
              </p>
              <p className="mt-1 text-sm text-yellow-700">
                Email sending is forcibly disabled while demo mode is active to prevent abuse.
                Configuration is still available but no emails will be sent.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Status Card */}
      <div className={`border-l-4 p-4 ${isEnabled ? 'bg-green-50 border-green-400' : 'bg-gray-50 border-gray-400'}`}>
        <div className="flex">
          <div className="flex-shrink-0">
            {isEnabled ? (
              <CheckCircleIcon className="h-5 w-5 text-green-400" />
            ) : (
              <XCircleIcon className="h-5 w-5 text-gray-400" />
            )}
          </div>
          <div className="ml-3">
            <p className="text-sm font-medium text-gray-800">
              Email Service: {isEnabled ? 'Enabled' : 'Disabled'}
            </p>
            {config?.hasApiKey && (
              <p className="mt-1 text-xs text-gray-600">
                API Key: {config.apiKeyPreview}
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Quota Usage */}
      {isEnabled && quota && (
        <div className="bg-white shadow rounded-lg p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
              <ChartBarIcon className="h-5 w-5" />
              Email Quota Usage
            </h3>
            <span className="text-sm text-gray-500">
              {new Date(quota.periodStart).toLocaleDateString()} - {new Date(quota.periodEnd).toLocaleDateString()}
            </span>
          </div>

          <div className="space-y-2">
            <div className="flex justify-between text-sm">
              <span>Emails sent this month:</span>
              <span className="font-semibold">
                {quota.sent.toLocaleString()} / {quota.limit ? quota.limit.toLocaleString() : '∞'}
              </span>
            </div>
            {quota.limit && (
              <>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div
                    className={`h-2 rounded-full ${quota.percentage > 90 ? 'bg-red-600' : quota.percentage > 75 ? 'bg-yellow-600' : 'bg-green-600'}`}
                    style={{ width: `${Math.min(quota.percentage, 100)}%` }}
                  ></div>
                </div>
                <div className="text-xs text-gray-500 text-right">
                  {quota.percentage}% used
                </div>
              </>
            )}
            <div className="text-sm text-gray-600">
              Tier: <span className="font-medium capitalize">{quota.tier}</span>
            </div>
          </div>
        </div>
      )}

      {/* Actions */}
      <div className="bg-white shadow rounded-lg p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Actions</h3>
        <div className="space-y-3">
          {!isEnabled ? (
            <button
              onClick={() => setShowWizard(true)}
              disabled={demoMode?.enabled}
              className="w-full flex justify-center items-center gap-2 py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
            >
              <Cog6ToothIcon className="h-5 w-5" />
              {demoMode?.enabled ? 'Configuration Disabled in Demo Mode' : 'Configure Email Service'}
            </button>
          ) : (
            <>
              <button
                onClick={() => setShowWizard(true)}
                disabled={demoMode?.enabled}
                className="w-full flex justify-center items-center gap-2 py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:bg-gray-100 disabled:cursor-not-allowed"
              >
                <Cog6ToothIcon className="h-5 w-5" />
                {demoMode?.enabled ? 'Configuration Disabled in Demo Mode' : 'Edit Configuration'}
              </button>
              <button
                onClick={handleDisableEmail}
                disabled={demoMode?.enabled}
                className="w-full flex justify-center items-center gap-2 py-2 px-4 border border-red-300 rounded-md shadow-sm text-sm font-medium text-red-700 bg-white hover:bg-red-50 disabled:bg-gray-100 disabled:cursor-not-allowed"
              >
                <XCircleIcon className="h-5 w-5" />
                Disable Email Service
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default EmailSettings;
